import React, {useEffect} from 'react';
import AddIcon from '@mui/icons-material/Add';
import {
    Alert,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    LinearProgress,
    Stack,
    Typography,
} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {useQueries} from "@tanstack/react-query";
import {createBoardQuery, getBoardsQuery} from "../services/taskService";
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {PageHeader} from "../components/layouts/PageHeader";
import {canEditBoard} from "../types/task";
import {queryKeys} from '../data/queryKeys';
import {apiErrorMessage, isNetworkError} from '../utilities/apiError';

const BoardsPage: React.FC = () => {
    const navigate = useNavigate();

    const [
        {data, isLoading},
    ] = useQueries({
        queries: [
            {
                queryKey: queryKeys.boards(),     // cache key
                queryFn: getBoardsQuery,     // promise-returning function
                staleTime: 1000 * 30,    // (optional) consider data fresh for 30s
                refetchOnWindowFocus: false,   // no focus refetch
                refetchOnReconnect: false,     // no reconnect refetch
            },
        ]
    });

    const boards = data?.boards ?? [];
    const kioskBoardId = data?.kioskBoardId ?? null;

    // Kiosk-restricted accounts skip the board picker entirely — there's
    // nothing for them to choose, so drop them straight onto their board.
    useEffect(() => {
        if (kioskBoardId) {
            navigate(`/boards/${kioskBoardId}`, {replace: true});
        }
    }, [kioskBoardId, navigate]);

    // Why a board could not be made (an account can only own so many), shown above the boards.
    const [createError, setCreateError] = React.useState('');

    const createBoard = async () => {
        setCreateError('');
        try {
            const boardId = await createBoardQuery('New Board');
            navigate(`/boards/${boardId}`);
        } catch (error) {
            setCreateError(isNetworkError(error) ? 'Network error. Please try again later.' : apiErrorMessage(error, "Couldn't create the board."));
        }
    }

    if (kioskBoardId) {
        // Redirecting via the effect above — render nothing so the picker never flashes.
        return null;
    }

    return (
        <Box>
            <PageHeader
                eyebrow="Your workspace"
                title="Your boards"
                subtitle={isLoading ? undefined : `${boards.length} shared ${boards.length === 1 ? 'space' : 'spaces'} to keep everyday plans together.`}
                actions={
                    <Button variant="contained" startIcon={<AddIcon/>} onClick={createBoard}>
                        New board
                    </Button>
                }
            />

            {createError && <Alert severity="warning" onClose={() => setCreateError('')} sx={{mb: 2}}>{createError}</Alert>}

            {!isLoading && boards.length === 0 && (
                <Stack alignItems="center" spacing={2} sx={{py: 8, textAlign: 'center'}}>
                    <ViewKanbanIcon sx={{fontSize: 48, color: 'text.disabled'}}/>
                    <Typography variant="h6">No boards yet</Typography>
                    <Typography color="text.secondary">Create a board to start organising tasks.</Typography>
                    <Button variant="contained" startIcon={<AddIcon/>} onClick={createBoard}>
                        Create your first board
                    </Button>
                </Stack>
            )}

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))',
                    gap: 2,
                }}
            >
                {!isLoading && boards.map((board) => {
                    const total = board.taskCount ?? 0;
                    const done = board.completedCount ?? 0;
                    const allDone = total > 0 && done === total;

                    return (
                        <Card
                            variant="outlined"
                            key={board.id}
                            sx={{
                                borderRadius: 2, transition: 'border-color 160ms ease, box-shadow 160ms ease', '&:hover': {borderColor: 'primary.main', boxShadow: '0 6px 22px rgba(37,59,54,0.06)'},
                            }}
                        >
                            <CardActionArea onClick={() => navigate(`/boards/${board.id}`)} sx={{height: '100%'}}>
                                <CardContent sx={{p: 2.5}}>
                                    <Stack spacing={2}>
                                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                            <Box sx={{display: 'flex', p: 1, bgcolor: 'primary.light', borderRadius: 1.25}}><ViewKanbanIcon color="primary"/></Box>
                                            <Typography variant="h6" component="h2" sx={{flexGrow: 1, minWidth: 0, overflowWrap: 'anywhere', pt: 0.5}}>
                                                {board.title}
                                            </Typography>
                                            {board.viewerRole && (
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={board.viewerRole}
                                                    color={canEditBoard(board.viewerRole) ? 'primary' : 'default'}
                                                    sx={{textTransform: 'capitalize', fontSize: 11}}
                                                />
                                            )}
                                        </Stack>

                                        <Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={total > 0 ? (done / total) * 100 : 0}
                                                color="success"
                                                aria-label={`${board.title}: task completion`}
                                                sx={{height: 5, borderRadius: 2, mb: 1.25}}
                                            />
                                            <Typography variant="caption" color="text.secondary">
                                                {total === 0
                                                    ? 'No tasks yet'
                                                    : `${done} of ${total} ${total === 1 ? 'task' : 'tasks'} done`}
                                            </Typography>
                                        </Box>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{pt: 1.5, borderTop: 1, borderColor: 'divider'}}><Typography variant="body2" sx={{fontWeight: 600, color: allDone ? 'success.main' : 'primary.main'}}>{allDone ? 'All taken care of' : total ? `${total - done} remaining` : 'Start planning'}</Typography><ArrowForwardIcon sx={{fontSize: 18, color: 'text.secondary'}}/></Stack>
                                    </Stack>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    );
                })}
            </Box>
        </Box>
    );
};

export default BoardsPage;
